/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Lib dependencies
import {
	ActionIcon,
	Drawer,
	Flex,
	Group,
	NumberFormatter,
	Radio,
	RangeSlider,
	Stack,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Styles
import classes from './Filter.module.css';

// UI local components
import { Button, Text, Title } from '@components/v2/ui';

// Icons
import { IconX } from '@tabler/icons-react';

// Utils
import { MultiSelect } from '@components/v2/ui/MultiSelect/MultiSelect';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import useStore from '@store/index';
import { getCurrencyUnit } from '@utils/currency-units';
import { cloneDeep } from '@utils/functions';
import isNil from 'lodash/isNil';
import { useCountryCodes } from 'shared/api/get-country-codes';
import { useAdditionalityProject } from '../../hooks/useAdditionalityProjects';

/* -------------------------------------------------------------------------- */
/*                                   Types                                     */
/* -------------------------------------------------------------------------- */

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	'location.countryCode'?: { $in: string[] };
	technology?: string[];
	'financing.pledged_euro'?: RangeFilter;
	'quantity.remaining_aco2'?: RangeFilter;
	[key: string]: unknown;
};

type TFilterOffsetsProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: TFilterOffsetsProps) => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	const { checkRole } = useAuthorization();

	const [fundedPartSlider, setFundedPartValueSlider] =
		useState<[number, number]>([0, 100000]);

	const { setAdditionalityProjectsQueryParams } =
		useAdditionalityProject();

	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});

	/* ******************************** HELPERS ******************************* */

	const handleReset = () => {
		setAdditionalityProjectsQueryParams({});
		setFundedPartValueSlider([0, 100000]);
		setQueryParams({});
		onApplyFilters({});
		onClose();
	};

	const { data: countryCodes } = useCountryCodes(
		'additionalities'
	);
	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const { setAdditionalityProjectsPage } = useStore();
	const handleApplyFilters = () => {
		setAdditionalityProjectsQueryParams(queryParams);
		setAdditionalityProjectsPage(1);
		onApplyFilters(queryParams);
		onClose();
	};

	useEffect(() => {
		if (opened) {
			setQueryParams(cloneDeep(value ?? {}));

			const priceFilter =
				value?.['financing.pledged_euro'];
			setFundedPartValueSlider([
				priceFilter?.$gte ?? 0,
				priceFilter?.$lte == 1000000000000000
					? 100000
					: (priceFilter?.$gte ?? 100000),
			]);
		}
	}, [opened, value]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			{/* Header */}
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
				pb={'var(--mantine-spacing-xl)'}
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>

			{/* Drawer content */}
			<Flex
				flex={1}
				direction={'column'}
				justify="space-between"
			>
				<Stack gap={'var(--mantine-spacing-sm)'}>
					{!checkRole([
						ROLES.BRAND_ADMIN,
						ROLES.BRAND_STAFF,
					]) && (
						<Radio.Group
							label={t('global.status')}
							value={
								queryParams?.active === true
									? 'active'
									: queryParams?.active === false
										? 'inActive'
										: 'all'
							}
							onChange={(value) => {
								if (value === 'all') {
									const { active, ...rest } = queryParams;
									setQueryParams(rest);
								} else {
									setQueryParams({
										...queryParams,
										active: value === 'active',
									});
								}
							}}
						>
							<Group mt="xs">
								<Group mt="xs">
									<Radio
										value="all"
										label={t('global.all')}
									/>
									<Radio
										value="active"
										label={t('global.active')}
									/>
									<Radio
										value="inActive"
										label={t('global.inactive')}
									/>
								</Group>
							</Group>
						</Radio.Group>
					)}

					<MultiSelect
						placeholder={t('global.country')}
						w={'100%'}
						comboboxProps={{ withinPortal: false }}
						data={filteredCountries}
						clearable
						size="lg"
						value={
							queryParams['location.countryCode']
								? (queryParams['location.countryCode']
										?.$in ?? [])
								: []
						}
						searchable
						onChange={(value) => {
							if (value.length === 0) {
								const _cloneQueryParams =
									cloneDeep(queryParams);
								delete _cloneQueryParams[
									'location.countryCode'
								];
								setQueryParams({ ..._cloneQueryParams });
								return;
							}

							setQueryParams({
								...queryParams,
								'location.countryCode': { $in: value },
							});
						}}
					/>

					<MultiSelect
						placeholder={t('global.technology')}
						w={'100%'}
						comboboxProps={{ withinPortal: false }}
						data={PROJECT_TECHNOLOGIES}
						clearable
						size="lg"
						value={queryParams.technology ?? []}
						searchable
						onChange={(value) => {
							if (value.length === 0) {
								const _cloneQueryParams =
									cloneDeep(queryParams);
								delete _cloneQueryParams['technology'];
								setQueryParams(_cloneQueryParams);
								return;
							}
							setQueryParams({
								...queryParams,
								technology: value,
							});
						}}
					/>

					<Stack>
						<Text
							c="var(--mantine-color-igluTextSecondary-9)"
							variant="button"
							mb={'20px'}
						>
							{t('global.fundedPart')}
						</Text>
						<RangeSlider
							labelAlwaysOn
							label={(value) =>
								value === 0 || value === 100000 ? null : (
									<NumberFormatter
										thousandSeparator=" "
										value={value}
										suffix={' ' + getCurrencyUnit('EUR')}
										decimalScale={2}
									/>
								)
							}
							marks={[
								{ value: 0, label: '0€' },
								{ value: 100000, label: '100k€' },
							]}
							color="var(--mantine-color-igluPrimary-9)"
							value={fundedPartSlider}
							min={0}
							max={100000}
							onChange={(value) => {
								setFundedPartValueSlider(value);

								if (
									value[0] === 0 &&
									value[1] === 100000
								) {
									const cloneQueryParams =
										cloneDeep(queryParams);
									delete cloneQueryParams[
										'financing.pledged_euro'
									];
									setQueryParams(cloneQueryParams);
								} else {
									setQueryParams({
										...queryParams,
										'financing.pledged_euro': {
											$gte: value[0],
											$lte:
												value[1] === 100000
													? 1000000000000000
													: value[1],
										},
									});
								}
							}}
						/>
					</Stack>
				</Stack>

				{/* Buttons */}
				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
				>
					<Button
						fullWidth
						variant={'outline'}
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						onClick={handleApplyFilters}
						fullWidth
						variant={'primary'}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
